import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { TOKENS } from '../state/colors';
import { fs } from '../state/typography';
import { formatMoneda } from '../state/format';
import { BolsaCentro } from '../types';

interface TopClientsProps {
  cotizaciones: BolsaCentro[];
}

export const TopClients: React.FC<TopClientsProps> = ({ cotizaciones }) => {
  const top = useMemo(() => {
    const porCliente = new Map<string, number>();
    for (const b of cotizaciones) {
      const nombre = b.cliente_razon_social ?? 'Sin cliente';
      porCliente.set(nombre, (porCliente.get(nombre) ?? 0) + b.total);
    }
    const totalGeneral = Array.from(porCliente.values()).reduce((a, v) => a + v, 0) || 1;
    return Array.from(porCliente.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([nombre, monto], i) => ({
        ranking: String(i + 1).padStart(2, '0'),
        nombre,
        monto,
        porcentaje: (monto / totalGeneral) * 100,
      }));
  }, [cotizaciones]);

  const max = top[0]?.monto ?? 1;

  return (
    <Box data-testid="top-clients">
      {/* SPEC-105 (CH-01) — título "Top clientes" ahora lo provee el SectionCard que envuelve
          este componente en ResumenTab; se elimina el duplicado. */}
      {top.length === 0 ? (
        <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(13), color: TOKENS.faint }}>Sin datos aún.</Typography>
      ) : (
        top.map((c) => (
          <Box
            key={c.nombre}
            sx={{
              display: 'grid',
              gridTemplateColumns: '20px 1fr 86px 40px',
              alignItems: 'center',
              gap: '10px',
              borderBottom: `1px solid ${TOKENS.hair}`,
              padding: '10px 0',
            }}
          >
            <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(11), color: TOKENS.faint }}>{c.ranking}</Typography>
            <Box>
              <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(13), fontWeight: 500, color: TOKENS.ink, marginBottom: '4px' }}>
                {c.nombre}
              </Typography>
              <Box sx={{ height: 6, borderRadius: '999px', backgroundColor: TOKENS.wash, overflow: 'hidden' }}>
                <Box
                  sx={{
                    height: '100%',
                    width: `${(c.monto / max) * 100}%`,
                    borderRadius: '999px',
                    background: `linear-gradient(90deg, ${TOKENS.purpleSoft}, ${TOKENS.purple})`,
                  }}
                />
              </Box>
            </Box>
            <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(12.5), fontWeight: 600, color: TOKENS.ink, textAlign: 'right' }}>
              {formatMoneda(c.monto)}
            </Typography>
            <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(11), color: TOKENS.muted, textAlign: 'right' }}>
              {c.porcentaje.toFixed(0)}%
            </Typography>
          </Box>
        ))
      )}
    </Box>
  );
};
